/**
 * SME Climate Journey Workspace — 6-Step Conversion-Optimized Flow
 * Route: /sme/journey
 *
 * Layout: 3-column
 *   Left rail (240px) — step navigation
 *   Main canvas (flex) — active step content
 *   Right panel (320px) — live insight panel
 *
 * Steps (V2 — speed-first):
 *   1. Intent ("Your Goal")
 *   2. Data Input (fast — scope labels only)
 *   3. Result & Readiness (merged: snapshot + CRS + benchmark)
 *   4. Actions (merged: opportunities + plan + CRS impact)
 *   5. Finance Brief (hero narrative + download)
 *   6. Submit & Download (reports + FI + optional evidence upgrade)
 *
 * Design principle: speed → insight → narrative → conversion
 * Technical transparency (factors, hash) accessible but not front-loaded.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CRSPanel from "@/components/CRSPanel";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import client from "@/api/client";
import { toast } from "sonner";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  CheckCircle2, Lock, RefreshCw, Download, Share2, History,
  ChevronDown, ChevronRight, ArrowRight, AlertCircle, Info,
  Building2, BarChart2, Activity, Shield, Target, GitBranch,
  ListChecks, DollarSign, FileText, Zap, Save, AlertTriangle,
  TrendingDown, TrendingUp, Check, Upload, X, Plus, Edit3,
  Gauge, Award, BookOpen
} from "lucide-react";

const BRAND   = "#0F172A";
const TEAL    = "#059669";
const BG_PAGE = "#F8FAFC";

// ── Step definitions ───────────────────────────────────────────────────────
const STEPS = [
  { id: 1, key: "intent",    label: "Your Goal",             icon: Zap,        requires: [] },
  { id: 2, key: "data",      label: "Data Input",            icon: BarChart2,  requires: ["intent"] },
  { id: 3, key: "result",    label: "Result & Readiness",    icon: Activity,   requires: ["data"] },
  { id: 4, key: "actions",   label: "Actions",               icon: Target,     requires: ["data"] },
  { id: 5, key: "narrative", label: "Finance Brief",         icon: FileText,   requires: ["data"] },
  { id: 6, key: "submit",    label: "Submit & Download",     icon: DollarSign, requires: ["data"] },
];

// ── Status badge ───────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const CONFIG = {
    submission_ready: { label: "Ready for Financing Review", classes: "bg-emerald-100 text-emerald-700" },
    partially_ready:  { label: "Ready for Financing Review", classes: "bg-emerald-100 text-emerald-700" },
    in_progress:      { label: "In Progress",                classes: "bg-blue-100 text-blue-700"      },
  };
  const c = CONFIG[status] || CONFIG.in_progress;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${c.classes}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {c.label}
    </span>
  );
}

// ── TOP BAR ───────────────────────────────────────────────────────────────
function JourneyTopBar({ org, submissionStatus, onRecalculate, onSave, busy, currentVersion }) {
  const navigate = useNavigate();
  return (
    <div className="h-14 bg-white border-b border-slate-200 flex items-center px-5 gap-4 sticky top-0 z-30 flex-shrink-0"
      data-testid="journey-top-bar">
      {/* Left */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <img src="https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png"
          alt="DeCarbonIQ" className="h-6" style={{ filter: "brightness(0)" }} />
        <div className="w-px h-5 bg-slate-200" />
        <span className="text-xs font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">SME</span>
        {org && <span className="text-xs font-semibold text-slate-900 hidden md:block">{org.name}</span>}
      </div>

      {/* Center */}
      <div className="flex-1 flex justify-center">
        {submissionStatus && <StatusBadge status={submissionStatus} />}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={onRecalculate} disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40"
          data-testid="recalculate-btn">
          {busy ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Recalculate
        </button>
        <button onClick={async () => {
            const id = window._assessmentId;
            if (!id) { toast.warning("Complete your journey first."); return; }
            const token = localStorage.getItem("decarboniq_token");
            try {
              const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sme/finance-brief/${id}`,
                { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) throw new Error();
              const blob = await res.blob();
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "SME-Climate-Brief.pdf";
              a.click();
              URL.revokeObjectURL(a.href);
              toast.success("Climate Brief downloaded.");
            } catch { toast.error("Download failed — please retry."); }
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-[#0F172A] text-white rounded-xl hover:bg-slate-800"
          data-testid="download-btn">
          <Download size={12} /> Download Report
        </button>
      </div>
    </div>
  );
}

// ── LEFT STEP RAIL ────────────────────────────────────────────────────────
function JourneyStepRail({ activeStep, completedSteps, onStepClick }) {
  return (
    <div className="hidden md:flex w-[240px] flex-shrink-0 bg-white border-r border-slate-100 h-full overflow-y-auto sticky top-14"
      data-testid="step-rail">
      <div className="p-4">
        <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-3 px-2">Journey Steps</p>
        <div className="space-y-0.5">
          {STEPS.map((s, i) => {
            const done    = completedSteps.includes(s.key);
            const active  = activeStep === s.key;
            const blocked = false; // simplified: all steps accessible
            const SIcon   = s.icon;

            return (
              <button
                key={s.key}
                onClick={() => !blocked && onStepClick(s.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium text-left transition-all group ${
                  active   ? "bg-teal-50 text-teal-700 border border-teal-200" :
                  done     ? "text-emerald-600 hover:bg-emerald-50" :
                  blocked  ? "text-slate-300 cursor-not-allowed" :
                             "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                }`}
                data-testid={`step-nav-${s.key}`}>
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  active  ? "bg-teal-100" :
                  done    ? "bg-emerald-100" :
                  blocked ? "bg-slate-100" : "bg-slate-100 group-hover:bg-slate-200"
                }`}>
                  {done
                    ? <Check size={11} className="text-emerald-600" strokeWidth={3} />
                    : blocked
                      ? <Lock size={10} className="text-slate-300" />
                      : <SIcon size={11} className={active ? "text-teal-600" : "text-slate-400"} />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate leading-tight">{s.id}. {s.label}</p>
                </div>
                {active && <ChevronRight size={10} className="text-teal-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── RIGHT INSIGHT PANEL ───────────────────────────────────────────────────
function LiveInsightPanel({ impact, submissionStatus, eligibility }) {
  const hasData = impact?.state === "calculated";
  const anomalyCount = impact?.anomalies?.length || 0;
  const bm = impact?.benchmark;

  const CONFIDENCE_COLOR = {
    high:   "text-emerald-600 bg-emerald-50",
    medium: "text-amber-600 bg-amber-50",
    low:    "text-red-600 bg-red-50",
  };
  const BM_COLOR = {
    above_average: "text-red-600",
    at_average:    "text-amber-600",
    below_average: "text-emerald-600",
    unknown:       "text-slate-400",
  };

  return (
    <div className="w-[320px] flex-shrink-0 bg-white border-l border-slate-100 h-full overflow-y-auto sticky top-14 hidden xl:block"
      data-testid="insight-panel">
      <div className="p-4 space-y-4">
        <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Live Insights</p>

        {/* Emissions Snapshot */}
        <div className="bg-[#0F172A] rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Emissions</p>
          {hasData ? (
            <>
              <p className="text-2xl font-bold text-white tabular-nums leading-none">{impact.total_tco2e}</p>
              <p className="text-xs text-slate-400 mt-0.5">tCO₂e per year</p>
              <div className="mt-2 pt-2 border-t border-slate-800">
                <p className="text-xs text-slate-400">Main source</p>
                <p className="text-xs font-semibold text-teal-400 mt-0.5 truncate">
                  {impact.dominant_source} ({impact.dominant_pct}%)
                </p>
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-400">Enter your data to see emissions</p>
          )}
        </div>

        {/* Circularity */}
        {hasData && (
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Circularity Score</p>
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 flex-shrink-0">
                <svg width="40" height="40" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="15" fill="none" stroke="#E2E8F0" strokeWidth="5" />
                  <circle cx="20" cy="20" r="15" fill="none" stroke={TEAL} strokeWidth="5"
                    strokeDasharray={`${(impact.circularity_score/100)*94.2} 94.2`}
                    strokeDashoffset="23.6" strokeLinecap="round" />
                  <text x="20" y="24" textAnchor="middle" fill={BRAND} fontSize="10" fontWeight="700">{impact.circularity_score}</text>
                </svg>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-900">{impact.circularity_score}/100</p>
                <p className="text-xs text-slate-400">
                  {impact.circularity_score >= 70 ? "Good coverage" :
                   impact.circularity_score >= 50 ? "Improving" : "Expand data sources"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Data Confidence */}
        {hasData && (
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Data Confidence</p>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${CONFIDENCE_COLOR[impact.confidence_label] || CONFIDENCE_COLOR.medium}`}>
                {impact.confidence_label}
              </span>
              <span className="text-xs text-slate-500">{impact.data_confidence}%</span>
            </div>
          </div>
        )}

        {/* Benchmark */}
        {hasData && bm && bm.your_intensity && (
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Benchmark Position</p>
            <p className={`text-xs font-bold mb-1 ${BM_COLOR[bm.status] || "text-slate-500"}`}>{bm.status_label}</p>
            <div className="space-y-1.5">
              {[
                ["Your intensity", bm.your_intensity],
                ["Industry avg",   bm.industry_avg],
                ["Best in class",  bm.best_in_class],
              ].map(([lbl, val]) => (
                <div key={lbl} className="flex justify-between text-xs">
                  <span className="text-slate-400">{lbl}</span>
                  <span className="font-semibold text-slate-700">{val} {bm.unit}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Anomalies */}
        {hasData && impact?.anomalies?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs text-amber-700 uppercase tracking-wider font-bold mb-2">
              {impact.anomalies.length} Alert{impact.anomalies.length !== 1 ? "s" : ""}
            </p>
            {impact.anomalies.slice(0, 2).map(a => (
              <div key={a.id} className="mb-2">
                <p className="text-xs font-semibold text-amber-800">{a.title}</p>
                <p className="text-xs text-amber-600 mt-0.5">{a.fix}</p>
              </div>
            ))}
          </div>
        )}

        {/* Next best action */}
        {submissionStatus && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
            <p className="text-xs text-teal-600 uppercase tracking-wider font-bold mb-1">Your Status</p>
            <p className="text-xs text-teal-800 font-semibold">
              {(submissionStatus === "submission_ready" || submissionStatus === "partially_ready")
                ? "All steps verified — download your report or request financing."
                : "Enter your data to calculate emissions and unlock your opportunities."}
            </p>
          </div>
        )}

        {!hasData && (
          <div className="text-center py-4">
            <Activity size={24} className="text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Insights appear after data is submitted</p>
          </div>
        )}

        {/* Financing Quick View */}
        {eligibility && (
          <div className={`rounded-xl p-4 border ${
            eligibility.status === "finance_ready"   ? "bg-emerald-50 border-emerald-200" :
            eligibility.status === "partially_ready" ? "bg-emerald-50 border-emerald-200" :
                                                        "bg-slate-50 border-slate-200"
          }`} data-testid="insight-finance-status">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1.5">Financing</p>
            <div className="flex items-center justify-between mb-1">
              <p className={`text-xs font-bold ${
                eligibility.status === "finance_ready" ? "text-emerald-700" :
                eligibility.status === "partially_ready" ? "text-emerald-700" : "text-slate-500"
              }`}>{(eligibility.status === "partially_ready") ? "Ready for Financing Review" : eligibility.status_label}</p>
              <p className="text-xs font-bold text-slate-600">{eligibility.financing_confidence?.display_score ?? eligibility.financing_confidence?.score}%</p>
            </div>
            {eligibility.indicative_range && (
              <p className="text-xs text-slate-500">
                ${(eligibility.indicative_range.low/1000).toFixed(0)}k–${(eligibility.indicative_range.high/1000).toFixed(0)}k indicative
              </p>
            )}
            {eligibility.financing_confidence?.next_unlock && (eligibility.status !== "finance_ready" && eligibility.status !== "partially_ready") && (
              <p className="text-xs text-teal-600 mt-1.5 leading-snug">→ {eligibility.financing_confidence.next_unlock.split("(")[0].trim()}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── STEP CARD WRAPPER ─────────────────────────────────────────────────────
function StepSection({ id, stepKey, number, title, icon: Icon, description, children, footerActions }) {
  return (
    <SectionCard noPadding className="mb-5" testid={`step-section-${stepKey}`}>
      <div id={`step-${stepKey}`}>
        <div className="px-6 py-5 border-b border-slate-100 flex items-start gap-4">
          <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon size={18} className="text-teal-600" />
          </div>
          <div>
            <p className="text-xs text-teal-500 font-bold uppercase tracking-widest">Step {number}</p>
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
          </div>
        </div>
        <div className="px-6 py-5">{children}</div>
        {footerActions && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-3">{footerActions}</div>
        )}
      </div>
    </SectionCard>
  );
}

// ── STEP 1: Business Profile ──────────────────────────────────────────────
function Step1_Profile({ org, onNext }) {
  const SECTORS = ["Energy / Gas", "Manufacturing", "Construction", "Agriculture", "Transport / Logistics", "Waste / Circularity", "Services / Office-based", "Other"];
  if (!org) return (
    <div className="text-center py-6">
      <RefreshCw size={18} className="animate-spin text-teal-400 mx-auto mb-2" />
      <p className="text-xs text-slate-400">Loading profile...</p>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {[
          ["Company Name", org.name || "—"],
          ["Sector",       org.sector || "—"],
          ["Country",      org.country || "—"],
          ["Employees",    org.employee_count ? `${org.employee_count} people` : "—"],
        ].map(([l, v]) => (
          <div key={l} className="bg-slate-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">{l}</p>
            <p className="text-sm font-semibold text-slate-900 mt-0.5">{v}</p>
          </div>
        ))}
      </div>
      {org.description && (
        <div className="bg-slate-50 rounded-xl px-4 py-3">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Description</p>
          <p className="text-xs text-slate-600">{org.description}</p>
        </div>
      )}
      <div className="flex items-center gap-2">
        {["Energy / Gas", "Industrial", "Transport"].map(tag => (
          <span key={tag} className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-full">{tag}</span>
        ))}
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-2">
        <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-700">Your profile is confirmed. You're onboarding a company with built-in decarbonization leverage.</p>
      </div>
    </div>
  );
}

// ── STEP 2: Stable Input Row (defined OUTSIDE to prevent remounting on state change) ──
// CRITICAL: Must be outside Step2_DataInput — if defined inside, React remounts the
// <input> on every keypress (losing focus, breaking number entry).
function NumberInputRow({ label, hint, scope, unit, value, onChange, testId }) {
  const SCOPE_COLOR = {
    "1": "text-teal-600 bg-teal-50",
    "2": "text-blue-600 bg-blue-50",
    "3": "text-purple-600 bg-purple-50",
    "—": "text-slate-400 bg-slate-50",
  };
  return (
    <div className="flex items-center gap-4 bg-slate-50 rounded-xl px-4 py-3">
      <div className="flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-xs font-semibold text-slate-900">{label}</p>
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${SCOPE_COLOR[scope] || SCOPE_COLOR["—"]}`}>
            {scope !== "—" ? `Scope ${scope}` : "—"}
          </span>
        </div>
        <p className="text-xs text-slate-400">{hint}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <input
          type="number"
          min="0"
          value={value}
          onChange={onChange}
          className="w-28 h-9 px-3 border border-slate-200 rounded-xl text-sm text-right focus:outline-none focus:border-teal-400 bg-white font-mono"
          data-testid={testId}
        />
        <span className="text-xs text-slate-400 w-14">{unit}</span>
      </div>
    </div>
  );
}


// ── STEP 2: Data Input — Scope 1, 2, and simplified Scope 3 ──────────────
function Step2_DataInput({ journey, onCalculated, calculateRef }) {
  const [period,  setPeriod]  = useState("annual");
  const [form,    setForm]    = useState({
    fleet: "", gen: "", elec: "", gas: "",
    // Scope 3 — simplified
    logistics_km: "", waste_tonnes: ""
  });
  const [preview, setPreview] = useState(null);
  const [busy,    setBusy]    = useState(false);
  const raw = journey?.raw_inputs;
  const fp  = journey?.footprint;
  const isEdit = !!(fp);

  useEffect(() => {
    if (raw) {
      setForm(f => ({
        ...f,
        fleet: raw.fleet_diesel_liters    ? String(raw.fleet_diesel_liters)    : "",
        gen:   raw.generator_diesel_liters ? String(raw.generator_diesel_liters) : "",
        elec:  raw.electricity_kwh         ? String(raw.electricity_kwh)         : "",
        gas:   raw.gas_throughput_tonnes   ? String(raw.gas_throughput_tonnes)   : "",
      }));
    }
  }, [raw?.fleet_diesel_liters]);

  // Live preview — backend is the ONLY source of truth for actual emissions.
  // This preview ONLY tells the user "you have filled in inputs" — not the actual number.
  // The real calculation runs on backend via Calculate button (V1 engine, factor DB).
  useEffect(() => {
    const hasData = Object.values(form).some(v => parseFloat(v) > 0);
    setPreview(hasData ? "pending" : null);  // "pending" = inputs present, not yet calculated
  }, [form, period]);

  const handleCalculate = async () => {
    const hasData = Object.values(form).some(v => parseFloat(v) > 0);
    if (!hasData) { toast.warning("Enter at least one value to continue."); return; }
    setBusy(true);
    try {
      const mult = period === "monthly" ? 12 : 1;
      const res = await client.post("/sme/submit-emissions", {
        assessment_id:           journey?.assessment_id,
        fleet_diesel_liters:     form.fleet ? parseFloat(form.fleet) * mult : null,
        generator_diesel_liters: form.gen   ? parseFloat(form.gen)   * mult : null,
        electricity_kwh:         form.elec  ? parseFloat(form.elec)  * mult : null,
        gas_throughput_tonnes:   form.gas   ? parseFloat(form.gas)   * mult : null,
        logistics_km:            form.logistics_km ? parseFloat(form.logistics_km) * mult : null,
        waste_tonnes:            form.waste_tonnes  ? parseFloat(form.waste_tonnes) * mult : null,
        grid_factor_source:      "nigeria_default",
      });
      toast.success(isEdit ? "Data updated — recalculating..." : "Data saved. Your impact is calculated.");
      onCalculated(res.data);
    } catch { toast.error("Calculation failed. Please check your inputs."); }
    setBusy(false);
  };

  // Expose handleCalculate to parent via ref (fixes external button triggering calculation)
  useEffect(() => {
    if (calculateRef) calculateRef.current = handleCalculate;
  });

  const SCOPE12 = [
    { key: "fleet", label: "Fleet diesel",    unit: "L/yr", hint: "Total diesel used by operational vehicles in one year",              scope: "1" },
    { key: "gen",   label: "Generator diesel",unit: "L/yr", hint: "Total diesel consumed by generators in one year",                   scope: "1" },
    { key: "elec",  label: "Grid electricity",unit: "kWh/yr", hint: "Annual electricity consumption from the national grid",           scope: "2" },
    { key: "gas",   label: "Gas throughput",  unit: "tonnes", hint: "LPG/CNG distributed — tracked for intensity, not added to Scope 1", scope: "—" },
  ];
  const SCOPE3 = [
    { key: "logistics_km", label: "Business logistics / freight", unit: "km/yr",    hint: "Total km of delivery/transport by company vehicles or contractors", scope: "3" },
    { key: "waste_tonnes", label: "Waste generated",              unit: "tonnes/yr", hint: "Solid waste sent to landfill or incineration (optional)",          scope: "3" },
  ];

  return (
    <div className="space-y-4">
      {/* Period toggle */}
      <div className="flex items-center gap-3">
        <p className="text-xs text-slate-500">I'm entering:</p>
        <div className="flex rounded-xl border border-slate-200 overflow-hidden">
          {[["annual","Annual"], ["monthly","Monthly"]].map(([v,l]) => (
            <button key={v} onClick={() => setPeriod(v)}
              className={`px-4 py-2 text-xs font-semibold transition-all ${period===v ? "bg-teal-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              data-testid={`period-${v}`}>{l}</button>
          ))}
        </div>
        {isEdit && <span className="text-xs text-slate-400 italic">Updating creates a new version</span>}
      </div>

      {/* Scope 1 & 2 */}
      <div>
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2 px-1">Scope 1 — Direct + Scope 2 — Energy</p>
        <div className="space-y-2">
          {SCOPE12.map(f => (
            <NumberInputRow
              key={f.key}
              label={f.label}
              hint={f.hint}
              scope={f.scope}
              unit={f.unit}
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              testId={`ws-input-${f.key}`}
            />
          ))}
        </div>
      </div>

      {/* Scope 3 — simplified */}
      <div>
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2 px-1">Scope 3 — Value Chain (optional)</p>
        <div className="space-y-2">
          {SCOPE3.map(f => (
            <NumberInputRow
              key={f.key}
              label={f.label}
              hint={f.hint}
              scope={f.scope}
              unit={f.unit}
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              testId={`ws-input-${f.key}`}
            />
          ))}
        </div>
      </div>

      {/* Inputs ready indicator — backend is the source of truth, no frontend math */}
      {preview === "pending" && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl px-5 py-4 flex items-center gap-4">
          <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-bold text-teal-700">Inputs ready — click Calculate</p>
            <p className="text-xs text-teal-500">
              {[form.fleet,form.gen,form.elec,form.logistics_km,form.waste_tonnes].filter(v=>v&&parseFloat(v)>0).length} sources entered.
              All emissions are computed by the V1 engine (GHG Protocol, factor DB — not estimated here).
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── STEP 3: Impact Overview ────────────────────────────────────────────────

// ── STEP 1: Intent — "Your Goal" (NEW) ───────────────────────────────────────
// 3 cards. No form. 1 click. Sets context for everything downstream.
const INTENT_OPTIONS = [
  {
    key:     "Get financing",
    icon:    DollarSign,
    title:   "Get Climate Financing",
    desc:    "Green loans, climate funds, DFI programmes, or concessional capital",
    color:   "border-teal-400 bg-teal-50 text-teal-700",
    hover:   "hover:border-teal-500 hover:bg-teal-100",
  },
  {
    key:     "Reduce costs",
    icon:    TrendingDown,
    title:   "Reduce Operating Costs",
    desc:    "Cut energy bills, fuel spend, and waste through efficiency improvements",
    color:   "border-amber-400 bg-amber-50 text-amber-700",
    hover:   "hover:border-amber-500 hover:bg-amber-100",
  },
  {
    key:     "Meet ESG requirements",
    icon:    Shield,
    title:   "Meet ESG or Reporting Requirements",
    desc:    "Supplier ESG requests, corporate sustainability targets, or regulatory disclosure",
    color:   "border-blue-400 bg-blue-50 text-blue-700",
    hover:   "hover:border-blue-500 hover:bg-blue-100",
  },
];

function Step1_Intent({ journey, intent, onIntentSet }) {
  const [selected, setSelected] = React.useState(intent || null);
  const [saving,   setSaving]   = React.useState(false);

  const handleSelect = async (key) => {
    setSelected(key);
    setSaving(true);
    try {
      await client.post("/sme/set-intent", {
        intent:        key,
        assessment_id: journey?.assessment_id,
      });
      onIntentSet(key);
    } catch { /* non-blocking */ }
    setSaving(false);
  };

  return (
    <div>
      <div className="space-y-3" data-testid="intent-options">
        {INTENT_OPTIONS.map(opt => {
          const Icon   = opt.icon;
          const active = selected === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => handleSelect(opt.key)}
              data-testid={`intent-${opt.key.replace(/\s+/g, "-").toLowerCase()}`}
              className={`w-full flex items-start gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all
                ${active ? opt.color + " ring-2 ring-offset-1 ring-current" : "border-slate-200 bg-white " + opt.hover}`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? "bg-current/10" : "bg-slate-100"}`}>
                <Icon size={16} className={active ? "" : "text-slate-500"} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${active ? "" : "text-slate-900"}`}>{opt.title}</p>
                <p className={`text-xs mt-0.5 ${active ? "opacity-80" : "text-slate-400"}`}>{opt.desc}</p>
              </div>
              {active && <div className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 rounded-full bg-current" />
              </div>}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="mt-4 flex items-center gap-2 px-4 py-2.5 bg-slate-50 rounded-xl">
          <div className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
          <p className="text-xs text-slate-500 flex-1">
            Goal set: <span className="font-bold text-slate-700">{selected}</span>
          </p>
          <p className="text-xs text-slate-400">Continue to Data Input</p>
        </div>
      )}
    </div>
  );
}


// ── STEP 3: Result & Readiness (merged: Impact + CRS + Benchmark) ─────────────
function StepResult({ impact, journeyData, navigate }) {
  // ALL hooks MUST be at top — before any early returns
  const [showFactors, setShowFactors] = React.useState(false);

  // Use journeyData.footprint (always loaded from backend) as primary.
  // Fall back to impact (current session) — same data, both from V1 engine.
  const fp   = journeyData?.footprint || impact?.footprint;
  const cars = journeyData?.cars_score;
  const peer = journeyData?.peer_benchmark;
  const factors = journeyData?.audit_provenance?.factor_summary || [];
  const gridFactor = journeyData?.audit_provenance?.grid_factor;

  if (!fp?.total_tco2e) {
    return (
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center">
          <Activity size={16} /> Enter data first to see your results
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">

        {/* ── Section 1: Climate Snapshot ─────────────────────────────── */}
        <div className="bg-[#0F172A] rounded-xl p-5">
          <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-3">Your Climate Snapshot</p>
          <div className="flex items-end gap-3 mb-4">
            <span className="text-4xl font-bold text-white tabular-nums">{fp.total_tco2e}</span>
            <div className="mb-1">
              <p className="text-sm font-bold text-slate-400">tCO₂e / year</p>
              <p className="text-xs text-slate-600">{fp.scope_1 > 0 ? `Scope 1: ${fp.scope_1}` : ""} {fp.scope_2 > 0 ? `· Scope 2: ${fp.scope_2}` : ""} {fp.scope_3 > 0 ? `· Scope 3: ${fp.scope_3}` : ""}</p>
            </div>
          </div>
          {/* Activity breakdown */}
          {fp.by_category?.length > 0 && (
            <div className="space-y-1.5">
              {fp.by_category.filter(c => (c.kgco2e || 0) > 0).slice(0, 4).map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                    c.scope === 1 ? "bg-orange-900/40 text-orange-400" :
                    c.scope === 2 ? "bg-blue-900/40 text-blue-400" : "bg-purple-900/40 text-purple-400"
                  }`}>Scope {c.scope}</span>
                  <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500/60 rounded-full"
                      style={{ width: `${((c.kgco2e||0) / (fp.total_tco2e * 1000) * 100).toFixed(0)}%` }} />
                  </div>
                  <span className="text-xs text-slate-400 w-20 text-right truncate">{c.label}</span>
                  <span className="text-xs text-slate-300 font-mono w-12 text-right">{c.tco2e}t</span>
                </div>
              ))}
            </div>
          )}
          {/* Factor provenance — hover-to-reveal, not front-and-center */}
          <button onClick={() => setShowFactors(v => !v)}
            className="mt-3 text-xs text-slate-600 hover:text-slate-400 flex items-center gap-1">
            <ChevronDown size={9} className={showFactors ? "rotate-180" : ""} />
            {showFactors ? "Hide" : "View"} emission factors used
          </button>
          {showFactors && (
            <div className="mt-2 space-y-0.5 border-t border-white/5 pt-2">
              {gridFactor?.value && (
                <p className="text-xs text-slate-500">
                  Grid electricity: {gridFactor.value} kgCO₂e/kWh — {gridFactor.source || "IEA 2023"}
                </p>
              )}
              {factors.slice(0, 3).map((f, i) => (
                <p key={i} className="text-xs text-slate-500">
                  {f.category}: {f.name?.substring(0, 35)} — {f.value} {f.unit}
                </p>
              ))}
              <p className="text-xs text-slate-600 italic mt-1">
                Factor library: {journeyData?.audit_provenance?.factor_library_ver || "DCI-Africa-V1.0"}
              </p>
            </div>
          )}
        </div>

        {/* ── Section 2: Readiness (CRS) ──────────────────────────────── */}
        {cars && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-2">Your Readiness</p>
            <CRSPanel carsData={cars} peerBenchmark={peer} />
          </div>
        )}

        {/* ── Section 3: Industry Benchmark ───────────────────────────── */}
        {fp.industry_benchmark && (
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-3">vs Industry</p>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs text-slate-600">
                  Your emissions: <span className="font-bold text-slate-900">{fp.total_tco2e} tCO₂e/yr</span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Industry range: <span className="font-semibold">{fp.industry_benchmark.avg_range}</span>
                  {" "}· {fp.industry_benchmark.sector_label}
                </p>
              </div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                fp.industry_benchmark.vs_average === "more_efficient"
                  ? "bg-emerald-100 text-emerald-700"
                  : fp.industry_benchmark.vs_average === "at_average"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-red-100 text-red-700"
              }`}>
                {fp.industry_benchmark.label}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── STEP 4: Actions (merged: Opportunities + Action Plan + CRS impact) ────────
function StepActions({ actions, cars }) {
  // ALL hooks at top
  const [expanded, setExpanded] = React.useState(null);
  const totalReduction   = actions?.reduce((s, a) => s + (a.co2_reduction || 0), 0) || 0;
  const totalSavings     = actions?.reduce((s, a) => s + (a.annual_savings || 0), 0) || 0;
  const crsComponents    = cars?.components || {};

  // Map each action to the CRS component it improves
  const ACTION_CRS_MAP = {
    solar_pv:          { comp: "implementation_ease", pts: "+4 pts" },
    renewable_energy:  { comp: "implementation_ease", pts: "+4 pts" },
    fuel_switch:       { comp: "financial_potential",  pts: "+6 pts" },
    transport:         { comp: "financial_potential",  pts: "+6 pts" },
    energy_efficiency: { comp: "implementation_ease", pts: "+3 pts" },
    efficiency:        { comp: "implementation_ease", pts: "+3 pts" },
  };

  if (!actions?.length) {
    return (
      <div>
        <p className="text-sm text-slate-400 py-8 text-center">No actions yet — enter data first.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-3">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-4">
          {[
            ["Total reduction potential", `${totalReduction.toFixed(1)} tCO₂e/yr`, "text-teal-600"],
            ["Annual savings potential", `$${(totalSavings/1000).toFixed(0)}k/yr`, "text-emerald-600"],
          ].map(([l,v,c]) => (
            <MetricCard key={l} label={l} value={v} color={c} />
          ))}
        </div>

        {/* Action cards with CRS impact */}
        {actions.slice(0, 5).map((a, i) => {
          const isOpen  = expanded === i;
          const crsHint = ACTION_CRS_MAP[a.action_type] || ACTION_CRS_MAP[a.type];
          return (
            <div key={i} className="border border-slate-200 rounded-xl overflow-hidden"
              data-testid={`action-card-${i}`}>
              <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                onClick={() => setExpanded(isOpen ? null : i)}>
                <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target size={13} className="text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-900 truncate">
                    {a.title || a.action_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                  <p className="text-xs text-slate-400">
                    −{a.co2_reduction || 0}t CO₂e/yr · ~${((a.capex_required||a.capex_usd||0)/1000).toFixed(0)}k est. CAPEX · {a.payback_years}yr payback
                  </p>
                </div>
                {crsHint && (
                  <span className="text-xs font-bold text-teal-600 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
                    {crsHint.pts} CRS
                  </span>
                )}
                <ChevronDown size={12} className={`text-slate-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-3 pt-1 bg-slate-50 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">{a.description}</p>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[
                      ["Annual savings", `$${((a.annual_savings||0)/1000).toFixed(1)}k`],
                      ["ROI", `${a.roi_pct?.toFixed(0) || 0}%`],
                      ["Confidence", a.confidence_level || "medium"],
                    ].map(([l,v]) => (
                      <div key={l} className="text-center bg-white rounded-lg p-2">
                        <p className="text-xs text-slate-400">{l}</p>
                        <p className="text-xs font-bold text-slate-900">{v}</p>
                      </div>
                    ))}
                  </div>
                  {/* SDG Badges */}
                  {a.sdg_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.sdg_tags.map(sdg => (
                        <span key={sdg.id}
                          className="text-xs font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {sdg.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {crsHint && (
                    <p className="text-xs text-teal-600 mt-2">
                      Implementing this action improves your {crsHint.comp.replace(/_/g, " ")} score ({crsHint.pts}).
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── CARBON MARKET PATHWAYS ────────────────────────────────────────────────
function CarbonPathways({ journeyData }) {
  const navigate = useNavigate();
  const [enrolled, setEnrolled] = React.useState(false);
  const [enrolling, setEnrolling] = React.useState(false);
  const fp  = journeyData?.footprint;
  const crs = journeyData?.cars_score;
  const totalReduction = (journeyData?.opportunity?.total_opportunity_tco2e || 0);

  const PATHWAYS = [
    {
      id:          "insetting",
      name:        "Insetting (Supply Chain)",
      eligibility: "High",
      eligColor:   "bg-emerald-100 text-emerald-700 border-emerald-300",
      reason:      "Direct emissions reduction within your value chain. Position as supply chain decarbonization for corporate buyers — no credit sales required.",
      requirements:["Documented client fuel-switching", "Baseline emissions established", "Buyer agreement or MOU"],
      cta:         null,
    },
    {
      id:          "vcm",
      name:        "Voluntary Carbon Market (VCM)",
      eligibility: "Medium",
      eligColor:   "bg-amber-100 text-amber-700 border-amber-300",
      reason:      `Your identified reductions (${totalReduction}t CO₂e/yr) are suitable for VCM exploration. Aggregation required to reach viable project scale.`,
      requirements:["Third-party additionality study", "Methodology validation (Gold Standard / Verra)", "Aggregation with other SMEs"],
      cta:         "join",
    },
    {
      id:          "poa",
      name:        "Programmatic (PoA)",
      eligibility: "Low",
      eligColor:   "bg-slate-100 text-slate-600 border-slate-300",
      reason:      "Insufficient standalone scale. A Programme of Activities bundles multiple SMEs — join an aggregated portfolio to participate.",
      requirements:["Portfolio bundling (min. 500t CO₂e/yr aggregate)", "Program Coordinating Entity (PCE)", "MRV across all participants"],
      cta:         "join",
    },
    {
      id:          "article6",
      name:        "Article 6 (International Compliance)",
      eligibility: "Not eligible",
      eligColor:   "bg-red-50 text-red-600 border-red-200",
      reason:      "Insufficient scale and requires national authorization. Future pathway via aggregation and national alignment.",
      requirements:["Host country DNA authorization", "Letter of Authorization (LoA)", "Bilateral agreement"],
      cta:         null,
    },
  ];

  const handleJoin = async () => {
    setEnrolling(true);
    try {
      await import("@/api/client").then(m => m.default.post("/sme/aggregation-interest", { note: "Via Finance Brief pathways" }));
      setEnrolled(true);
      toast.success("Interest registered — our team will contact you within 48 hours.");
    } catch { toast.error("Could not register interest. Please try again."); }
    setEnrolling(false);
  };

  if (!fp?.total_tco2e) return null;

  return (
    <div className="space-y-3" data-testid="carbon-pathways">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Carbon Market Pathways</p>
        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Indicative — based on your current data</span>
      </div>

      {PATHWAYS.map(p => (
        <div key={p.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`pathway-card-${p.id}`}>
          <div className="px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-snug">{p.reason}</p>
            </div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ml-3 ${p.eligColor}`}>
              {p.eligibility}
            </span>
          </div>
          <div className="px-4 pb-3 bg-slate-50 border-t border-slate-100">
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Requirements</p>
            <div className="flex flex-wrap gap-1.5">
              {p.requirements.map((r, i) => (
                <span key={i} className="text-xs text-slate-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded">{r}</span>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* CTA — Join Aggregated Carbon Program */}
      {!enrolled ? (
        <button
          onClick={() => navigate("/aggregation-enrollment")}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-teal-400 text-teal-700 bg-teal-50 text-sm font-bold rounded-xl hover:bg-teal-100 transition-colors"
          data-testid="join-aggregation-btn">
          <GitBranch size={14} /> Join Aggregated Carbon Program
        </button>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
          <CheckCircle2 size={14} className="text-emerald-600" />
          <p className="text-xs text-emerald-700 font-semibold">Interest registered — our team will contact you within 48 hours.</p>
        </div>
      )}
    </div>
  );
}

// ── STEP 5: Finance Narrative (hero output) ───────────────────────────────────
function StepNarrative({ journeyData, navigate }) {
  const narrative = journeyData?.finance_narrative;
  const fp        = journeyData?.footprint;
  const cars      = journeyData?.cars_score;
  const intent    = journeyData?.intent;

  const handleDownload = async () => {
    const id = journeyData?.assessment_id;
    if (!id) { toast.error("Complete data input first."); return; }
    try {
      const token = localStorage.getItem("decarboniq_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sme/finance-brief/${id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const org = journeyData?.organization || {};
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `${(org.name||"company").replace(/[^a-zA-Z0-9]/g,"-")}-ClimateBrief.pdf`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Finance Brief downloaded.");
    } catch { toast.error("Brief generation failed."); }
  };

  if (!narrative) {
    return (
      <div>
        <p className="text-sm text-slate-400 py-8 text-center">Enter your data to generate your finance brief.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {/* Hero narrative */}
        <div className="bg-[#0F172A] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-teal-500/20 rounded-xl flex items-center justify-center">
              <FileText size={14} className="text-teal-400" />
            </div>
            <div>
              <p className="text-xs text-teal-400 font-bold uppercase tracking-widest">Finance Narrative</p>
              <p className="text-xs text-slate-500">Goal: {intent || "Climate Finance"}</p>
            </div>
          </div>
          <p className="text-sm text-slate-200 leading-relaxed font-medium">
            {narrative}
          </p>
        </div>

        {/* Key metrics row */}
        {fp && (
          <div className="grid grid-cols-3 gap-2">
            {[
              ["Total Emissions", `${fp.total_tco2e} tCO₂e/yr`, "text-slate-900"],
              ["CRS Score", `${Math.round(cars?.score||0)}/100`, "text-teal-700"],
              ["CRS Band", cars?.band || "—", "text-teal-700"],
            ].map(([l,v,c]) => (
              <div key={l} className="bg-slate-50 rounded-xl px-3 py-2.5 text-center">
                <p className="text-xs text-slate-400 mb-0.5">{l}</p>
                <p className={`text-sm font-bold ${c}`}>{v}</p>
              </div>
            ))}
          </div>
        )}

        {/* Download button */}
        <button onClick={handleDownload}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 transition-colors"
          data-testid="download-finance-brief-sme">
          <Download size={14} /> Download Finance Brief (PDF)
        </button>
        <p className="text-xs text-slate-400 italic text-center">
          Bank-facing decision document — not a certification.
        </p>
        <div className="pt-2 border-t border-slate-100">
          <button onClick={() => navigate && navigate("/carbon-pathways")}
            className="w-full flex items-center justify-center gap-2 py-2.5 border border-teal-200 text-teal-700 text-xs font-bold rounded-xl hover:bg-teal-50"
            data-testid="view-pathways-btn">
            <GitBranch size={13} /> View Carbon Market Pathways →
          </button>
        </div>
      </div>
    </div>
  );
}


// ── STEP 6: Submit (merged: Financing + Reports + optional Upgrade) ───────────
function StepSubmit({ journey, navigate, onPdfDownload }) {
  const [showUpgrade,  setShowUpgrade]  = React.useState(false);
  const [downloading,  setDownloading]  = React.useState(false);
  const fp  = journey?.footprint;
  const crs = journey?.cars_score;
  const org = journey?.organization;
  const isComplete = fp?.total_tco2e > 0;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("decarboniq_token");
      const id    = journey?.assessment_id;
      if (!id) { toast.error("Assessment not found."); setDownloading(false); return; }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sme/finance-brief/${id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(org?.name || "Company").replace(/[^a-zA-Z0-9]/g,"-")}-ClimateBrief.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Climate Credentials downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Report generation failed — please retry.");
    }
    setDownloading(false);
  };

  return (
    <div className="space-y-4">
      {/* Primary: ONE report button */}
      <div className={`rounded-xl border-2 p-5 ${isComplete ? "border-teal-300 bg-teal-50" : "border-slate-200 bg-slate-50"}`}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {isComplete ? "Assessment Complete" : "Complete data entry to generate report"}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {isComplete
                ? `${fp.total_tco2e} tCO₂e/yr · CRS Band ${crs?.band || "—"} · ${org?.name || ""}`
                : "Enter your emissions data and click Calculate."}
            </p>
          </div>
          {isComplete && <span className="text-xs font-bold bg-teal-100 text-teal-700 px-2 py-1 rounded-full border border-teal-300 flex-shrink-0">Ready</span>}
        </div>
        <button onClick={handleDownload} disabled={!isComplete || downloading}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40"
          data-testid="download-climate-credentials">
          {downloading ? <RefreshCw size={14} className="animate-spin" /> : <FileText size={14} />}
          {downloading ? "Generating report…" : "Download Climate Credentials Report"}
        </button>
        {isComplete && <p className="text-xs text-slate-400 text-center mt-2 italic">One-page decision document — ready to share with banks and partners.</p>}
      </div>

      {/* FI submission */}
      {isComplete && (
        <button onClick={() => navigate && navigate("/financing")}
          className="w-full flex items-center gap-3 px-4 py-3 border border-teal-300 text-teal-700 bg-teal-50 rounded-xl hover:bg-teal-100"
          data-testid="submit-to-fi">
          <DollarSign size={14} />
          <div className="text-left">
            <p className="text-xs font-bold">Apply for Climate Financing</p>
            <p className="text-xs text-teal-600">Submit to financing pipeline for lender review</p>
          </div>
        </button>
      )}

      {/* Optional evidence upgrade — collapsed */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <button onClick={() => setShowUpgrade(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
          data-testid="upgrade-toggle">
          <div className="flex items-center gap-2">
            <Shield size={12} className="text-slate-400" />
            <p className="text-xs font-semibold text-slate-600">Upgrade Data Quality</p>
            <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">Optional</span>
          </div>
          <ChevronDown size={12} className={`text-slate-400 transition-transform ${showUpgrade?"rotate-180":""}`} />
        </button>
        {showUpgrade && (
          <div className="px-4 py-3 bg-white space-y-1.5">
            <p className="text-xs text-slate-500">Upload evidence to upgrade from <strong>Estimated → Measured</strong>. Improves CRS score and financing access.</p>
            {[["Fleet diesel","Fuel invoices"],["Electricity","Utility bills"]].map(([l,d]) => (
              <div key={l} className="flex items-start gap-2 text-xs text-slate-500">
                <span className="text-teal-500 font-bold">+</span><div><span className="font-bold text-slate-600">{l}: </span>{d}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



function Step3_Impact({ impact }) {
  if (!impact || impact.state !== "calculated") {
    return (
      <div className="text-center py-8">
        <Activity size={28} className="text-slate-200 mx-auto mb-2" />
        <p className="text-sm text-slate-400">Complete Step 2 to see your impact overview</p>
      </div>
    );
  }

  const cats = (impact.by_category || []).sort((a,b)=>b.kgco2e-a.kgco2e).slice(0,4);
  const total = impact.total_tco2e || 0;
  const bm = impact.benchmark;

  return (
    <div className="space-y-5">
      {/* Headline numbers */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Annual Emissions", value: total, unit: "tCO₂e", color: "text-slate-900" },
          { label: "Emission Intensity", value: impact.emission_intensity || "—", unit: "tCO₂e/emp", color: "text-teal-600" },
          { label: "Circularity Score", value: impact.circularity_score, unit: "/100", color: "text-emerald-600" },
        ].map(({ label, value, unit, color }) => (
          <MetricCard key={label} label={label} value={value} sub={unit} color={color} />
        ))}
      </div>

      {/* Source breakdown */}
      <div>
        <p className="text-xs font-semibold text-slate-900 mb-3">Emission Sources</p>
        <div className="space-y-2.5">
          {cats.map(cat => {
            const pct = Math.round(cat.kgco2e / (total * 1000) * 100);
            const friendly = {
              "Fleet Diesel (Scope 1)": "Fleet vehicles",
              "Generator Diesel (Scope 1)": "Generator fuel",
            };
            const label = friendly[cat.label] || cat.label.replace(/\s*\(.*\)/, "");
            return (
              <div key={cat.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">{label}</span>
                  <span className="font-bold text-slate-900">{cat.tco2e}t · {pct}%</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-teal-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Benchmark comparison */}
      {bm && (
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-900 mb-3">Benchmark — {bm.sector}</p>
          <div className="space-y-2">
            {[
              ["Your Intensity",  bm.your_intensity, bm.status === "below_average" ? "text-emerald-600" : bm.status === "above_average" ? "text-red-600" : "text-amber-600"],
              ["Industry Average",bm.industry_avg,   "text-slate-600"],
              ["Best in Class",   bm.best_in_class,  "text-emerald-600"],
            ].map(([lbl, val, color]) => (
              <div key={lbl} className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{lbl}</span>
                <span className={`text-sm font-bold ${color}`}>{val} <span className="text-xs font-normal text-slate-400">{bm.unit}</span></span>
              </div>
            ))}
          </div>
          <div className={`mt-3 pt-2 border-t border-slate-200 text-xs font-semibold ${
            bm.status === "below_average" ? "text-emerald-600" :
            bm.status === "above_average" ? "text-red-600" : "text-amber-600"
          }`}>{bm.status_label}</div>
        </div>
      )}

      {/* Insight */}
      {impact.insight && (
        <div className="bg-[#0F172A] rounded-xl px-5 py-4">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Key Insight</p>
          <p className="text-sm text-white leading-relaxed">{impact.insight}</p>
        </div>
      )}
    </div>
  );
}

// ── STEP 4: Evidence, Confidence & Anomalies ──────────────────────────────
function Step4_Evidence({ impact, anomaliesData, onEvidenceUploaded }) {
  const hasData = impact?.state === "calculated";
  const [evidenceList, setEvidenceList] = useState([]);
  const [uploading,    setUploading]    = useState({});

  const loadEvidence = async () => {
    try {
      const res = await client.get("/sme/workspace/evidence");
      setEvidenceList(res.data?.evidence || []);
    } catch {}
  };

  useEffect(() => { loadEvidence(); }, []);

  const handleUpload = async (category, file) => {
    if (!file) return;
    setUploading(p => ({...p, [category]: true}));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", category);
    try {
      await client.post("/sme/workspace/evidence/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${category} evidence uploaded. Your confidence score will improve.`);
      await loadEvidence();
      if (onEvidenceUploaded) onEvidenceUploaded();
    } catch {
      toast.error("Upload failed. Please check file format (PDF, Excel, CSV, image).");
    }
    setUploading(p => ({...p, [category]: false}));
  };

  const SOURCES = [
    { key: "electricity", label: "Grid Electricity",    hint: "Utility bills or meter readings (last 6–12 months)" },
    { key: "fuel",        label: "Fleet Diesel",        hint: "Fuel invoices or petrol receipts for fleet vehicles" },
    { key: "generator",   label: "Generator Diesel",    hint: "Generator fuel purchase records or invoices" },
    { key: "other",       label: "Supporting Document", hint: "Business registration, audit report, or other evidence" },
  ];

  const uploadedByCategory = evidenceList.reduce((acc, e) => { acc[e.type] = true; return acc; }, {});

  return (
    <div className="space-y-5">
      {/* Section A: Evidence Upload */}
      <div>
        <p className="text-sm font-semibold text-slate-900 mb-1">Supporting Evidence</p>
        <p className="text-xs text-slate-500 mb-3">
          Banks verify self-reported data against source documents. Each upload directly improves your financing confidence score.
        </p>
        {SOURCES.map(src => (
          <div key={src.key} className={`flex items-center justify-between rounded-xl px-4 py-3 mb-2 border ${
            uploadedByCategory[src.key] ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
          }`}>
            <div className="flex items-center gap-3 flex-1">
              {uploadedByCategory[src.key]
                ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                : <Upload size={14} className="text-slate-400 flex-shrink-0" />
              }
              <div>
                <p className="text-xs font-semibold text-slate-700">{src.label}</p>
                <p className="text-xs text-slate-400">{src.hint}</p>
                {uploadedByCategory[src.key] && (
                  <p className="text-xs text-emerald-600 font-semibold">✓ Document uploaded</p>
                )}
              </div>
            </div>
            <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer transition-all ${
              uploading[src.key] ? "opacity-50 cursor-not-allowed" :
              uploadedByCategory[src.key] ? "border border-emerald-200 text-emerald-600 hover:bg-white bg-white" :
              "border border-slate-200 text-slate-600 hover:bg-white bg-white"
            }`}
              data-testid={`upload-${src.key}`}>
              {uploading[src.key]
                ? <><RefreshCw size={11} className="animate-spin" /> Uploading...</>
                : uploadedByCategory[src.key]
                ? <><Upload size={11} /> Replace</>
                : <><Upload size={11} /> Upload</>
              }
              <input type="file" className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
                onChange={e => handleUpload(src.key, e.target.files?.[0])}
                disabled={!!uploading[src.key]} />
            </label>
          </div>
        ))}

        {/* Uploaded files list */}
        {evidenceList.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-2">Uploaded Documents ({evidenceList.length})</p>
            {evidenceList.slice(0, 5).map(e => (
              <div key={e.id} className="flex items-center gap-2 text-xs text-slate-500 py-0.5">
                <FileText size={10} />
                <span className="truncate">{e.file_name}</span>
                <span className="text-slate-300 flex-shrink-0 capitalize">{e.type}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-400 italic mt-2">
          Evidence is linked to your current calculation version. Accepted formats: PDF, Excel, CSV, Word, images.
        </p>
      </div>

      {/* Section B: Confidence */}
      {hasData && (
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-sm font-semibold text-slate-900 mb-3">Data Confidence</p>
          <div className="flex items-center gap-3 mb-3">
            <div className={`text-xs font-bold px-3 py-1 rounded-full capitalize ${
              impact.confidence_label === "high"   ? "bg-emerald-100 text-emerald-700" :
              impact.confidence_label === "medium" ? "bg-amber-100 text-amber-700" :
                                                     "bg-red-100 text-red-700"
            }`}>{impact.confidence_label || "Medium"}</div>
            <span className="text-sm font-semibold text-slate-900">{impact.data_confidence}%</span>
          </div>
          <div className="space-y-2">
            {[
              ["Completeness",      Math.round((impact.data_confidence || 0) * 1.1)],
              ["Evidence coverage", 40],
              ["Source quality",    Math.round((impact.data_confidence || 0) * 0.9)],
              ["Consistency",       Math.round((impact.data_confidence || 0) * 0.95)],
            ].map(([lbl, val]) => (
              <div key={lbl}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="text-slate-500">{lbl}</span>
                  <span className="font-semibold text-slate-700">{Math.min(100, val)}%</span>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.min(100, val)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section C: Anomalies */}
      {anomaliesData?.anomalies?.length > 0 ? (
        <div>
          <p className="text-sm font-semibold text-slate-900 mb-3">
            Detected Alerts
            <span className="ml-2 text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              {anomaliesData.anomalies.length} found
            </span>
          </p>
          <div className="space-y-3">
            {anomaliesData.anomalies.map(a => (
              <div key={a.id} className={`rounded-xl p-4 border ${
                a.severity === "high"   ? "bg-red-50 border-red-200" :
                a.severity === "medium" ? "bg-amber-50 border-amber-200" :
                                          "bg-slate-50 border-slate-200"
              }`}>
                <div className="flex items-start gap-2 mb-1.5">
                  <AlertTriangle size={13} className={
                    a.severity === "high" ? "text-red-500" : a.severity === "medium" ? "text-amber-500" : "text-slate-400"
                  } />
                  <p className="text-xs font-bold text-slate-900">{a.title}</p>
                </div>
                <p className="text-xs text-slate-500 mb-1.5">{a.why}</p>
                <p className="text-xs font-semibold text-teal-700">Fix: {a.fix}</p>
              </div>
            ))}
          </div>
        </div>
      ) : hasData ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-600" />
          <p className="text-xs text-emerald-700 font-semibold">No anomalies detected in your current dataset.</p>
        </div>
      ) : (
        <div className="text-center py-4 text-xs text-slate-400">Anomalies appear after calculation</div>
      )}
    </div>
  );
}

// ── STEP 5: Opportunities — Causally derived from emission sources ─────────
function Step5_Opportunities({ actions, onSelect, selectedOpps, impact }) {

  // Source driver labels — maps category_source to human label + causal trigger
  const SOURCE_DRIVER = {
    "fleet_diesel":      { label: "Fleet diesel emissions", color: "text-orange-600 bg-orange-50" },
    "generator_diesel":  { label: "Generator fuel",          color: "text-orange-600 bg-orange-50" },
    "electricity":       { label: "Grid electricity",        color: "text-blue-600 bg-blue-50"     },
    "client_emissions":  { label: "Client operations",       color: "text-emerald-700 bg-emerald-50"},
    "waste":             { label: "Waste generation",        color: "text-amber-700 bg-amber-50"   },
    "logistics":         { label: "Logistics",               color: "text-purple-600 bg-purple-50" },
  };

  const IMPACT_LABEL = {
    value_chain:    "Highest Value",
    transport:      "High Impact",
    renewable_energy: "High Impact",
    process:        "Good Impact",
  };
  const IMPACT_COLOR = {
    value_chain:    "bg-emerald-100 text-emerald-700",
    transport:      "bg-teal-100 text-teal-700",
    renewable_energy: "bg-blue-100 text-blue-700",
    process:        "bg-slate-100 text-slate-600",
  };

  if (!actions || actions.length === 0) {
    return (
      <div className="text-center py-8">
        <Target size={28} className="text-slate-200 mx-auto mb-2" />
        <p className="text-sm text-slate-400">Opportunities unlock after you calculate your emissions in Step 2</p>
        <p className="text-xs text-slate-300 mt-1">Each opportunity is derived directly from your emission sources</p>
      </div>
    );
  }

  const totalReduction = actions.filter((_, i) => selectedOpps.includes(i)).reduce((s, a) => s + (a.co2_reduction || 0), 0);
  const totalCapex     = actions.filter((_, i) => selectedOpps.includes(i)).reduce((s, a) => s + (a.capex_required || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {actions.length} opportunities derived from your emission sources. Select all that you plan to act on.
        </p>
        {selectedOpps.length > 0 && (
          <span className="text-xs font-bold text-teal-700 bg-teal-50 px-2 py-1 rounded-full">
            {selectedOpps.length} selected · {Math.round(totalReduction)}t · ${totalCapex.toLocaleString()}
          </span>
        )}
      </div>

      {actions.slice(0, 5).map((a, i) => {
        const isSelected = selectedOpps.includes(i);
        const driver     = SOURCE_DRIVER[a.category_source] || SOURCE_DRIVER["fleet_diesel"];
        const shortDesc  = a.description?.split(".")[0].replace(/\([^)]*\)/g, "").trim();
        return (
          <div key={i}
            className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${
              isSelected ? "border-teal-400 bg-teal-50/30 shadow-sm" : "border-slate-200 bg-white hover:border-teal-200"
            }`}
            onClick={() => onSelect(i)}
            data-testid={`ws-opportunity-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                {/* Causal label: "→ Driven by your X emissions" */}
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ArrowRight size={10} className="text-slate-300" />
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${driver.color}`}>
                    {driver.label}
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900">{shortDesc || a.action_type?.replace(/_/g," ")}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${IMPACT_COLOR[a.action_type] || "bg-slate-100 text-slate-500"}`}>
                  {IMPACT_LABEL[a.action_type] || "Impact"}
                </span>
                {isSelected && <CheckCircle2 size={16} className="text-teal-500" />}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400">Emission reduction</p>
                <p className="text-sm font-bold text-teal-600">{a.co2_reduction}t CO₂e/yr</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Est. CAPEX (indicative)</p>
                <p className="text-sm font-bold text-slate-700">
                  {a.capex_required > 0 ? `$${a.capex_required.toLocaleString()}` : "Minimal"}
                </p>
              </div>
              {a.annual_savings > 0 && (
                <div>
                  <p className="text-xs text-slate-400">Annual savings</p>
                  <p className="text-sm font-bold text-emerald-600">${a.annual_savings.toLocaleString()}/yr</p>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {selectedOpps.length === 0 && (
        <p className="text-xs text-amber-600 text-center">Select at least one opportunity to unlock Steps 6–8</p>
      )}
    </div>
  );
}

// ── STEP 6: Carbon Value & Pathway — Derived from selected opportunities ───
function Step6_Pathway({ actions, selectedOpps, onSelectPathway, selectedPathwayId }) {

  // Derive pathways from what the user actually selected in Step 5
  const selectedActions = actions.filter((_, i) => selectedOpps.includes(i));

  if (selectedActions.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-700 mb-1">No opportunities selected</p>
            <p className="text-xs text-amber-600">
              Go back to Step 5 and select which opportunities you plan to act on. Pathway options are derived from your selections.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totalReduction = selectedActions.reduce((s, a) => s + (a.co2_reduction || 0), 0);
  const totalCapex     = selectedActions.reduce((s, a) => s + (a.capex_required || 0), 0);
  const totalSavings   = selectedActions.reduce((s, a) => s + (a.annual_savings || 0), 0);
  const VCM_LOW        = Math.round(totalReduction * 15);
  const VCM_HIGH       = Math.round(totalReduction * 30);

  // Derive pathway options from selected action types
  const hasRenewable   = selectedActions.some(a => a.action_type === "renewable_energy" || a.action_type === "transport");
  const hasValueChain  = selectedActions.some(a => a.action_type === "value_chain");
  const isLargeScale   = totalReduction > 200;

  const pathways = [];

  // Internal savings — always available
  pathways.push({
    id:          "internal_savings",
    name:        "Internal Carbon Reduction",
    label:       "Immediate",
    why:         `Your selected actions generate $${totalSavings.toLocaleString()}/yr in guaranteed fuel savings. This is structured for financing without any carbon market participation.`,
    speed:       "Immediate — no certification needed",
    complexity:  "Low",
    readiness:   "Start now",
    carbon_value: null,
    guaranteed_savings: totalSavings,
    is_recommended: !hasValueChain && !isLargeScale,
  });

  // VCM — if renewable or large transport
  if (hasRenewable || hasValueChain || totalReduction > 50) {
    pathways.push({
      id:          "vcm",
      name:        "Voluntary Carbon Market (VCM)",
      label:       "Recommended for exploration",
      why:         (
        `Your selected actions represent ${Math.round(totalReduction)}t CO₂e/yr reduction — suitable for VCM project development exploration. ` +
        "This is indicative. Actual eligibility requires independent additionality assessment and methodology validation."
      ),
      speed:       "Indicative: 6–12 months subject to methodology review",
      complexity:  "Medium",
      readiness:   "Indicative — subject to third-party validation",
      carbon_value_low:  VCM_LOW,
      carbon_value_high: VCM_HIGH,
      disclaimer:  "DeCarbonIQ does not certify VCM eligibility. Final determination by the relevant standard body.",
      validation_required: ["Independent additionality study", "Methodology fit assessment", "Project boundary definition"],
      is_recommended: hasValueChain || hasRenewable,
    });
  }

  // Article 6 — only if large scale, with heavy disclaimer
  if (isLargeScale) {
    pathways.push({
      id:          "article6",
      name:        "Article 6 Sovereign Transaction",
      label:       "Future potential — sovereign process required",
      why:         `Scale of ${Math.round(totalReduction)}t CO₂e/yr is indicative of potential relevance to Article 6 exploration. However, Article 6 is a sovereign government process. DeCarbonIQ does not determine eligibility.`,
      speed:       "18–36 months subject to sovereign authorization",
      complexity:  "Very High",
      readiness:   "Indicative only",
      disclaimer:  "Article 6 eligibility is solely determined by host country governments and the UNFCCC. This is not an eligibility determination.",
      validation_required: ["Host country DNA authorization", "Letter of Authorization (LoA)", "Accredited MRV protocol", "Bilateral agreement registration"],
      is_recommended: false,
    });
  }

  const recommended = pathways.find(p => p.is_recommended) || pathways[0];

  return (
    <div className="space-y-4">
      {/* Summary from selected opportunities */}
      <div className="bg-[#0F172A] rounded-xl p-5 text-white">
        <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">From Your Selected Actions</p>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xl font-bold text-teal-400">{Math.round(totalReduction)}t</p>
            <p className="text-xs text-slate-400">CO₂e reduction/yr</p>
          </div>
          <div>
            <p className="text-xl font-bold text-white">${totalSavings.toLocaleString()}</p>
            <p className="text-xs text-slate-400">Guaranteed savings/yr</p>
          </div>
          <div>
            <p className="text-xl font-bold text-emerald-400">
              {VCM_LOW > 0 ? `$${(VCM_LOW/1000).toFixed(0)}k+` : "—"}
            </p>
            <p className="text-xs text-slate-400">Carbon value potential</p>
          </div>
        </div>
      </div>

      {/* Pathway cards */}
      <p className="text-xs font-semibold text-slate-500">How your reductions translate into carbon value:</p>
      <div className="space-y-3">
        {pathways.map(p => {
          const isSelected = selectedPathwayId === p.id;
          return (
            <div key={p.id}
              className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${
                isSelected ? "border-teal-400 bg-teal-50/30" :
                p.is_recommended ? "border-emerald-200" :
                "border-slate-200 hover:border-teal-200"
              }`}
              onClick={() => onSelectPathway(p.id)}
              data-testid={`pathway-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    p.is_recommended ? "bg-emerald-100 text-emerald-700" :
                    p.id === "article6" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{p.label}</span>
                  {isSelected && <CheckCircle2 size={15} className="text-teal-500" />}
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3">{p.why}</p>
              <div className="flex items-center gap-4">
                {p.speed && <div><p className="text-xs text-slate-400">Timeline</p><p className="text-xs font-semibold text-slate-600">{p.speed.split(":")[1]?.trim() || p.speed}</p></div>}
                <div><p className="text-xs text-slate-400">Complexity</p><p className="text-xs font-semibold text-slate-600">{p.complexity}</p></div>
                {p.carbon_value_low && <div><p className="text-xs text-slate-400">Carbon value</p><p className="text-xs font-bold text-emerald-600">${(p.carbon_value_low/1000).toFixed(0)}k–${(p.carbon_value_high/1000).toFixed(0)}k/yr</p></div>}
                {p.guaranteed_savings > 0 && <div><p className="text-xs text-slate-400">Savings</p><p className="text-xs font-bold text-blue-600">${p.guaranteed_savings.toLocaleString()}/yr</p></div>}
              </div>
              {p.disclaimer && (
                <p className="text-xs text-slate-300 mt-2 italic">{p.disclaimer}</p>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-300 text-center">
        Pathway routing is indicative. DeCarbonIQ does not certify carbon eligibility or represent government positions.
      </p>
    </div>
  );
}

// ── STEP 7: Action Plan — Aggregated from selected opportunities ──────────
function Step7_ActionPlan({ actions, selectedOpps }) {
  const selectedActions = actions.filter((_, i) => selectedOpps.includes(i));

  if (selectedActions.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-700 mb-1">No opportunities selected</p>
            <p className="text-xs text-amber-600">Go back to Step 5 and select which opportunities you plan to act on. Your action plan is built from those selections.</p>
          </div>
        </div>
      </div>
    );
  }

  // Aggregate totals
  const totalCapex     = selectedActions.reduce((s, a) => s + (a.capex_required || 0), 0);
  const totalSavings   = selectedActions.reduce((s, a) => s + (a.annual_savings || 0), 0);
  const totalReduction = selectedActions.reduce((s, a) => s + (a.co2_reduction || 0), 0);
  const avgPayback     = totalCapex > 0 ? Math.round(totalCapex / Math.max(totalSavings, 1) * 10) / 10 : null;

  const FRIENDLY = {
    value_chain:     "Client Fuel Switching Program",
    transport:       "Fleet Conversion to CNG/LPG",
    renewable_energy:"Solar Hybrid Installation",
    process:         "Generator Replacement",
  };

  return (
    <div className="space-y-5">
      {/* Action table */}
      <div>
        <p className="text-sm font-semibold text-slate-900 mb-3">Selected Actions</p>
        <div className="space-y-2">
          {selectedActions.map((a, i) => (
            <div key={i} className="bg-slate-50 rounded-xl px-4 py-3"
              data-testid={`action-row-${i}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-xs font-bold text-slate-900">{FRIENDLY[a.action_type] || a.action_type?.replace(/_/g," ")}</p>
                <span className="text-xs font-bold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded-full">{a.co2_reduction}t/yr</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><span className="text-slate-400">CAPEX: </span><span className="font-semibold text-slate-700">{a.capex_required > 0 ? `$${a.capex_required.toLocaleString()}` : "Minimal"}</span></div>
                <div><span className="text-slate-400">Savings: </span><span className="font-semibold text-emerald-600">${a.annual_savings.toLocaleString()}/yr</span></div>
                <div><span className="text-slate-400">Payback: </span><span className="font-semibold text-slate-600">{a.payback_years ? `${a.payback_years} yrs` : "—"}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Aggregate summary — feeds directly into Step 8 Financing */}
      <div className="bg-[#0F172A] rounded-xl p-5 text-white" data-testid="action-plan-totals">
        <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">Action Plan Summary → feeds Step 8</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-400">Total Est. CAPEX (indicative)</p>
            <p className="text-2xl font-bold text-white">${totalCapex.toLocaleString()}</p>
            <p className="text-xs text-slate-400">This becomes your financing need</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Total annual savings</p>
            <p className="text-2xl font-bold text-emerald-400">${totalSavings.toLocaleString()}</p>
            <p className="text-xs text-slate-400">Guaranteed fuel + energy savings</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Total emission reduction</p>
            <p className="text-2xl font-bold text-teal-400">{Math.round(totalReduction)}t</p>
            <p className="text-xs text-slate-400">tCO₂e per year</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Blended payback</p>
            <p className="text-2xl font-bold text-white">{avgPayback ? `${avgPayback} yrs` : "—"}</p>
            <p className="text-xs text-slate-400">From guaranteed savings only</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── UNLOCK FINANCING — Progression System ────────────────────────────────
function UnlockFinancing({ onNavigate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get("/sme/workspace/unlock-financing")
      .then(r => setData(r.data))
      .catch(() => {});
  }, []);

  if (!data) return null;

  const pct = Math.round((data.completed_count / data.total_count) * 100);
  const GATE_STYLE = {
    full:        { bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
    preliminary: { bar: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50 border-amber-200"   },
    blocked:     { bar: "bg-slate-300",   text: "text-slate-600",   bg: "bg-slate-50 border-slate-200"   },
  };
  const gs = GATE_STYLE[data.gate] || GATE_STYLE.blocked;

  return (
    <div className={`border rounded-xl p-5 ${gs.bg}`} data-testid="unlock-financing-panel">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">Unlock Financing</p>
          <p className={`text-xs ${gs.text} font-semibold mt-0.5`}>{data.gate_label}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Target: {data.target_score}% (lender review)</p>
          <p className="text-xs text-slate-400">Projected: ~{data.projected_score}% when complete</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-slate-400 mb-1">
          <span>{data.completed_count}/{data.total_count} requirements met</span>
          <span>{pct}% complete</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-700 ${gs.bar}`} style={{ width: `${pct}%` }} />
        </div>
        {data.gap > 0 && (
          <p className="text-xs text-slate-400 mt-1">{data.gap} points needed to reach lender-review threshold</p>
        )}
      </div>

      {/* Checklist items */}
      <div className="space-y-2">
        {data.items.map(item => (
          <div key={item.id}
            className={`flex items-start gap-3 rounded-xl px-4 py-3 cursor-pointer transition-all ${
              item.completed ? "bg-white/60 border border-emerald-200" : "bg-white border border-slate-200 hover:border-teal-300"
            }`}
            onClick={() => !item.completed && onNavigate && onNavigate(item.step)}
            data-testid={`unlock-item-${item.id}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
              item.completed ? "bg-emerald-500" : "bg-slate-100 border-2 border-slate-300"
            }`}>
              {item.completed && <Check size={10} className="text-white" strokeWidth={3} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${item.completed ? "text-emerald-700 line-through opacity-70" : "text-slate-900"}`}>
                {item.label}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">{item.why_bank}</p>
            </div>
            <div className="flex-shrink-0 text-right">
              {!item.completed && (
                <span className="text-xs font-bold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full">
                  +{item.pts_gain}%
                </span>
              )}
              {item.completed && (
                <span className="text-xs text-emerald-500 font-semibold">Done</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-300 text-center mt-3">{data.summary}</p>
    </div>
  );
}

// ── STEP 8: Financing — Derived strictly from Action Plan CAPEX ─────────
function Step8_Financing({ journey, actions, selectedOpps, eligibility }) {
  const navigate = useNavigate();
  const [showForm,   setShowForm]   = useState(false);
  const [form,       setForm]       = useState({ type: "capex_support", amount: "", purpose: "", timeline: "12" });
  const [busy,       setBusy]       = useState(false);
  const [submitted,  setSubmitted]  = useState(null);
  const [showImprov, setShowImprov] = useState(false);

  const el   = eligibility;
  const gate = el?.gate || "blocked";

  // Action plan aggregates
  const selectedActions = actions.filter((_, i) => selectedOpps.includes(i));
  const totalCapex      = selectedActions.reduce((s, a) => s + (a.capex_required || 0), 0);
  const totalSavings    = selectedActions.reduce((s, a) => s + (a.annual_savings || 0), 0);
  const totalReduction  = selectedActions.reduce((s, a) => s + (a.co2_reduction || 0), 0);

  // Set default amount from action plan CAPEX
  useEffect(() => {
    if (totalCapex > 0 && !form.amount) {
      setForm(f => ({ ...f, amount: String(totalCapex) }));
    }
  }, [totalCapex]);

  const STATUS_STYLE = {
    finance_ready:   { ring: "border-emerald-400", dot: "bg-emerald-500", text: "text-emerald-700" },
    partially_ready: { ring: "border-amber-400",   dot: "bg-amber-500",   text: "text-amber-700"   },
    not_ready:       { ring: "border-red-300",      dot: "bg-red-400",     text: "text-red-700"     },
  };
  const ss = STATUS_STYLE[el?.status] || STATUS_STYLE.not_ready;

  const ctaEnabled = gate !== "blocked";
  const ctaLabel   = gate === "full"        ? "Apply for Financing — Ready for Lender Review" :
                     gate === "preliminary"  ? "Submit for Preliminary Review" :
                                               "Not Yet Ready — Complete Required Steps";
  const ctaStyle   = gate === "full"
    ? "bg-[#0F172A] text-white hover:bg-slate-800 shadow-lg"
    : gate === "preliminary"
    ? "bg-amber-600 text-white hover:bg-amber-700"
    : "bg-slate-100 text-slate-400 cursor-not-allowed";

  const handleSubmit = async () => {
    if (!form.purpose.trim()) { toast.warning("Tell us how you'll use the funds."); return; }
    setBusy(true);
    try {
      const res = await client.post("/finance-requests/", {
        workspace_type: "sme", assessment_id: journey?.assessment_id,
        request_type: form.type,
        amount_requested_usd: form.amount ? parseFloat(form.amount) : null,
        purpose: form.purpose,
        timeline_months: form.timeline ? parseInt(form.timeline) : null,
      });
      await client.post(`/finance-requests/${res.data.request_id}/submit`);
      setSubmitted(res.data.request_id);
      toast.success("Finance application submitted to FI queue.");
    } catch { toast.error("Submission failed. Please try again."); }
    setBusy(false);
  };

  // Gate: No action plan = no financing
  if (selectedActions.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-700 mb-1">Action plan required before financing</p>
            <p className="text-xs text-amber-600">
              Financing is derived from your Action Plan. Go to Step 5 to select opportunities, then complete Step 7 to generate your CAPEX requirement. The financing amount will be pre-filled from your action plan.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="ws-finance-submitted">
        <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
        <p className="text-xl font-bold text-slate-900 mb-1">Application Submitted</p>
        <p className="text-sm text-slate-400 mb-1">Reference: <strong className="font-mono">{submitted}</strong></p>
        <p className="text-xs text-slate-400 mb-6">Your financing package has been sent to the FI review queue.</p>
        <button onClick={() => navigate("/financing")}
          className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700">
          <ArrowRight size={14} /> Track Application Status
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Financing derived from action plan */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <p className="text-xs text-slate-400 uppercase tracking-wide font-bold mb-3">Financing Need — Derived from Your Action Plan</p>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-slate-400">Required funding</p>
            <p className="text-2xl font-bold text-slate-900">${totalCapex.toLocaleString()}</p>
            <p className="text-xs text-slate-400">Total CAPEX ({selectedActions.length} actions)</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Annual savings</p>
            <p className="text-2xl font-bold text-emerald-600">${totalSavings.toLocaleString()}</p>
            <p className="text-xs text-slate-400">Guaranteed payback driver</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Payback period</p>
            <p className="text-2xl font-bold text-slate-700">
              {totalSavings > 0 ? `${Math.round(totalCapex / totalSavings * 10) / 10} yrs` : "—"}
            </p>
            <p className="text-xs text-slate-400">From fuel savings alone</p>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-3 italic">
          Financing need is pre-filled from your action plan. You may adjust the amount in the form below.
        </p>
      </div>

      {/* Finance eligibility badge */}
      {el && (
        <div className={`border-2 rounded-xl p-4 ${ss.ring}`} data-testid="finance-eligibility-badge">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${ss.dot}`} />
              <p className={`text-sm font-bold ${ss.text}`}>{el.status_label}</p>
            </div>
            <p className={`text-xs font-bold ${
              el.financing_confidence?.level === "high" ? "text-emerald-600" :
              el.financing_confidence?.level === "medium" ? "text-amber-600" : "text-slate-400"
            }`}>{el.financing_confidence?.display_label}</p>
          </div>
          <p className="text-xs text-slate-500 mt-1">{el.financing_confidence?.tagline}</p>
          {el.financing_confidence?.next_unlock && (
            <button onClick={() => setShowImprov(!showImprov)}
              className="mt-2 text-xs text-teal-600 font-semibold hover:underline flex items-center gap-1">
              <Zap size={9} /> {showImprov ? "Hide" : "How to improve"}
            </button>
          )}
          {showImprov && el.financing_confidence?.improvement_actions?.map((a, i) => (
            <p key={i} className="text-xs text-slate-400 mt-1 flex items-start gap-1">
              <span className="text-teal-500">→</span> {a}
            </p>
          ))}
        </div>
      )}

      {/* 2-layer range */}
      {el?.indicative_range && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-400 font-bold uppercase mb-1">Indicative Range <span className="text-slate-300 font-normal">Market signal</span></p>
            <p className="text-xl font-bold text-slate-900">
              ${(el.indicative_range.low/1000).toFixed(0)}k – ${(el.indicative_range.high/1000).toFixed(0)}k
            </p>
            <p className="text-xs text-slate-400 mt-1 italic">{el.indicative_range.note}</p>
          </div>
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
            <p className="text-xs text-teal-600 font-bold uppercase mb-1">Action Plan Estimate <span className="text-slate-400 font-normal">Project signal</span></p>
            <p className="text-xl font-bold text-teal-700">${totalCapex.toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1 italic">Derived from your {selectedActions.length} selected actions</p>
          </div>
        </div>
      )}

      {/* Qualifying / blocking */}
      {el?.qualifying_factors?.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            {el.qualifying_factors.map((f, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600 mb-1.5">
                <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0 mt-0.5" />{f}
              </div>
            ))}
          </div>
          {el.blocking_factors?.length > 0 && (
            <div>
              {el.blocking_factors.map((f, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600 mb-1.5">
                  <X size={11} className="text-red-400 flex-shrink-0 mt-0.5" />{f}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Apply CTA */}
      {!showForm ? (
        <div className="space-y-2">
          <button onClick={() => ctaEnabled && setShowForm(true)}
            disabled={!ctaEnabled}
            className={`w-full h-14 text-sm font-bold rounded-xl flex items-center justify-center gap-2 transition-all ${ctaStyle}`}
            data-testid="ws-apply-financing-btn">
            <DollarSign size={16} /> {ctaLabel}
          </button>
          {gate === "blocked" && (
            <p className="text-xs text-center text-red-500">Resolve blocking factors to unlock application</p>
          )}
          {gate === "preliminary" && (
            <p className="text-xs text-center text-amber-600">Score below 70% — preliminary review only. Improve your score for a stronger application.</p>
          )}
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-slate-900">Financing Application</p>
            <button onClick={() => setShowForm(false)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
          </div>
          <select value={form.type} onChange={e => setForm(f=>({...f,type:e.target.value}))}
            className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-teal-400">
            <option value="capex_support">Equipment / Capital Support</option>
            <option value="green_loan">Green Loan</option>
            <option value="grant">Grant / Subsidy</option>
            <option value="carbon_project_dev">Carbon Project Development</option>
            <option value="blended_finance">Blended Finance</option>
          </select>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400 mb-1">Amount (USD) — pre-filled from action plan</p>
              <input type="number" value={form.amount} onChange={e => setForm(f=>({...f,amount:e.target.value}))}
                className="w-full h-11 px-4 border border-teal-200 rounded-xl text-sm bg-teal-50 focus:outline-none focus:border-teal-400 font-semibold" />
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Timeline (months)</p>
              <input type="number" value={form.timeline} onChange={e => setForm(f=>({...f,timeline:e.target.value}))}
                className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-teal-400" />
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Use of funds</p>
            <textarea value={form.purpose} onChange={e => setForm(f=>({...f,purpose:e.target.value}))}
              placeholder={`Fund ${selectedActions.length} actions: ${selectedActions.map(a => a.action_type?.replace(/_/g," ")).join(", ")}`}
              rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:border-teal-400 bg-white"
              data-testid="ws-finance-purpose" />
          </div>
          <button onClick={handleSubmit} disabled={busy}
            className="w-full h-12 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="ws-finance-submit-btn">
            {busy ? <RefreshCw size={13} className="animate-spin" /> : <ArrowRight size={13} />}
            {busy ? "Submitting..." : "Submit Financing Application"}
          </button>
          {el?.platform_disclaimer && (
            <p className="text-xs text-slate-300 text-center">{el.platform_disclaimer}</p>
          )}
        </div>
      )}
    </div>
  );
}
// ── STEP 9: Reports & Submission — with working download ──────────────────
function Step9_Reports({ journey, submissionStatus }) {
  const navigate = useNavigate();
  const assessmentId = journey?.assessment_id;
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (fmt, label) => {
    if (!assessmentId) { toast.error("Complete your assessment first."); return; }
    setDownloading(fmt);
    try {
      const token = localStorage.getItem("decarboniq_token");
      const url = fmt === "pdf"
        ? `${process.env.REACT_APP_BACKEND_URL}/api/export-pdf/sme/${assessmentId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/sme/credentials-report/${assessmentId}?fmt=${fmt}`;
      const res  = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a    = document.createElement("a");
      const ext  = fmt === "pdf" ? "pdf" : fmt === "json" ? "json" : "txt";
      a.href     = URL.createObjectURL(blob);
      a.download = `decarbon-${label}-${new Date().toISOString().slice(0,10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${label} downloaded.`);
    } catch { toast.error("Download failed. Please try again."); }
    setDownloading(null);
  };

  const REPORTS = [
    { id: "credentials", label: "Climate Credentials Report",  desc: "Full climate position — investor-grade PDF",            fmt: "pdf",  ready: !!assessmentId, primary: true  },
    { id: "baseline",    label: "Emissions Baseline Report",   desc: "Scope 1/2/3 breakdown and methodology notes",           fmt: "json", ready: !!assessmentId, primary: false },
    { id: "opportunity", label: "Opportunity & Roadmap",       desc: "Top actions ranked by impact and value",                fmt: "text", ready: !!assessmentId, primary: false },
    { id: "finance",     label: "Finance Readiness Snapshot",  desc: "Summary for FI review — CAPEX, savings, reduction",     fmt: "json", ready: !!assessmentId, primary: false },
  ];

  return (
    <div className="space-y-5">
      {/* Submission status */}
      {submissionStatus && (
        <div className={`rounded-xl p-5 ${
          submissionStatus === "submission_ready" ? "bg-emerald-50 border border-emerald-200" :
          submissionStatus === "partially_ready"  ? "bg-amber-50 border border-amber-200" :
                                                    "bg-slate-50 border border-slate-200"
        }`}>
          <div className="flex items-center gap-3">
            {submissionStatus === "submission_ready"
              ? <CheckCircle2 size={20} className="text-emerald-600" />
              : <AlertCircle size={20} className={submissionStatus === "partially_ready" ? "text-amber-600" : "text-slate-400"} />
            }
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {submissionStatus === "submission_ready" ? "Ready for Financing Review — all checks passed" :
                 submissionStatus === "partially_ready"  ? "Ready for Financing Review" : "In Progress"}
              </p>
              <p className="text-xs text-slate-500">
                {(submissionStatus === "submission_ready" || submissionStatus === "partially_ready")
                  ? "Download and share your reports with finance partners."
                  : "Complete data entry to generate your reports."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Report cards with working download */}
      <div className="space-y-3">
        {REPORTS.map(r => (
          <div key={r.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-5 py-4"
            data-testid={`ws-report-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                {r.ready && (
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 flex-shrink-0">
                    <CheckCircle2 size={8} /> Ready
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400">{r.desc}</p>
            </div>
            <button
              onClick={() => handleDownload(r.fmt, r.id)}
              disabled={!r.ready || downloading === r.fmt}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl flex-shrink-0 ml-4 transition-all ${
                !r.ready           ? "bg-slate-100 text-slate-400 cursor-not-allowed" :
                downloading === r.fmt ? "bg-teal-100 text-teal-600 cursor-wait" :
                                     "bg-teal-600 text-white hover:bg-teal-700"
              }`}
              data-testid={`download-report-${r.id}`}>
              {downloading === r.fmt
                ? <><RefreshCw size={11} className="animate-spin" /> Downloading...</>
                : <><Download size={11} /> Download</>
              }
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        All reports are generated from your latest calculation version.
        <button onClick={() => navigate("/sme-reports")} className="text-teal-600 font-semibold ml-1 hover:underline">Full Reports Center →</button>
      </p>
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function SMEJourneyWorkspace() {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const { journeyData, refreshProgress } = useJourney();

  const [activeStep,      setActiveStep]      = useState("intent");
  const [intent,          setIntent]          = useState(null);  // "Get financing" | "Reduce costs" | "Meet ESG requirements"
  const [completedSteps,  setCompletedSteps]  = useState([]);
  const [impact,          setImpact]          = useState(null);
  const [anomaliesData,   setAnomaliesData]   = useState(null);
  const [pathwayData,     setPathwayData]     = useState(null);
  const [submissionStatus,setSubmissionStatus]= useState(null);
  const [selectedOpps,    setSelectedOpps]    = useState([]);
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [busy,            setBusy]            = useState(false);
  const [actions,         setActions]         = useState([]);
  const [eligibility,     setEligibility]     = useState(null);
  const [showWelcome,     setShowWelcome]      = useState(
    () => sessionStorage.getItem("decarboniq_new_user") === "sme"
  );

  // Ref to expose Step2_DataInput's handleCalculate to the parent's footer button
  const calculateRef = useRef(null);

  const org = journeyData?.organization;
  const fp  = journeyData?.footprint;

  // Store assessment_id globally for top bar download
  if (journeyData?.assessment_id) {
    window._assessmentId = journeyData.assessment_id;
  }

  // Load workspace data
  const loadWorkspaceData = useCallback(async () => {
    try {
      const [impRes, anomRes, pathRes, subRes, eligRes] = await Promise.allSettled([
        client.get("/sme/workspace/impact-overview"),
        client.get("/sme/workspace/anomalies"),
        client.get("/sme/workspace/pathway"),
        client.get("/sme/workspace/submission-status"),
        client.get("/sme/workspace/finance-eligibility"),
      ]);
      if (impRes.status  === "fulfilled") setImpact(impRes.value.data);
      if (anomRes.status === "fulfilled") setAnomaliesData(anomRes.value.data);
      if (pathRes.status === "fulfilled") {
        setPathwayData(pathRes.value.data);
        if (pathRes.value.data?.selected_pathway_id) {
          setSelectedPathway(pathRes.value.data.selected_pathway_id);
        }
      }
      if (subRes.status  === "fulfilled") setSubmissionStatus(subRes.value.data?.status);
      if (eligRes.status === "fulfilled") setEligibility(eligRes.value.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadWorkspaceData();
  }, [loadWorkspaceData]);

  // Derive actions from journey
  useEffect(() => {
    if (journeyData?.actions?.actions) {
      setActions(journeyData.actions.actions);
    }
  }, [journeyData]);

  // Derive completed steps
  useEffect(() => {
    const done = ["profile"]; // always done
    if (fp) done.push("data", "impact", "evidence");
    if (actions.length > 0) done.push("opportunities");
    if (selectedPathway) done.push("pathway");
    setCompletedSteps(done);
  }, [fp, actions, selectedPathway]);

  const handleCalculated = async (updatedJourney) => {
    refreshProgress();
    if (updatedJourney?.actions?.actions) setActions(updatedJourney.actions.actions);
    await loadWorkspaceData();
    setActiveStep("result");
    toast.success("Recalculated. Impact overview updated.");
  };

  const handleSelectPathway = async (pathwayId) => {
    setSelectedPathway(pathwayId);
    try {
      await client.post("/sme/workspace/pathway/select", { pathway_id: pathwayId });
      toast.success("Pathway selected.");
      await loadWorkspaceData();
    } catch { toast.error("Could not save pathway selection."); }
  };

  const handleOppSelect = (idx) => {
    setSelectedOpps(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const handleSave = () => toast.success("Draft saved.");
  const handleRecalculate = async () => {
    setBusy(true);
    try {
      await loadWorkspaceData();
      refreshProgress();
      toast.success("Recalculated with latest data.");
    } catch { toast.error("Recalculation failed."); }
    setBusy(false);
  };

  return (
    <div className="flex flex-col h-screen bg-[#F8FAFC]" data-testid="sme-workspace">
      {/* Top Bar */}
      <JourneyTopBar
        org={org}
        submissionStatus={submissionStatus}
        onRecalculate={handleRecalculate}
        onSave={handleSave}
        busy={busy}
        currentVersion={fp ? "1" : null}
      />

      {/* 3-column body */}
      <div className="flex flex-1 min-h-0">
      {/* Mobile step nav (hidden on md+) */}
      <div className="mobile-step-tabs md:hidden bg-white border-b border-slate-100 px-4 py-2 gap-1.5 flex-shrink-0">
        {STEPS.map(s => {
          const done   = completedSteps.includes(s.key);
          const active = activeStep === s.key;
          const SIcon  = s.icon;
          return (
            <button key={s.key} onClick={() => setActiveStep(s.key)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                active ? "bg-teal-600 text-white" :
                done   ? "bg-emerald-50 text-emerald-600 border border-emerald-200" :
                         "bg-slate-100 text-slate-400"
              }`}>
              {done ? <Check size={10} strokeWidth={3} /> : <SIcon size={10} />}
              <span className="whitespace-nowrap">{s.id}</span>
            </button>
          );
        })}
      </div>
      <JourneyStepRail
          activeStep={activeStep}
          completedSteps={completedSteps}
          onStepClick={setActiveStep}
        />

        {/* Main Canvas */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="">

            {/* ── Welcome "Start Here" Banner (new users only) ── */}
            {showWelcome && (
              <div className="bg-[#0F172A] rounded-xl px-5 py-4 mb-5 flex items-center gap-4"
                data-testid="welcome-start-banner">
                <div className="flex-1">
                  <p className="text-xs text-teal-400 font-bold uppercase tracking-wide mb-0.5">Your workspace is ready</p>
                  <p className="text-sm font-bold text-white">
                    Start here — enter your data to calculate your emissions
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Takes under 5 minutes. No expertise required.</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => {
                      setActiveStep("data");
                      setShowWelcome(false);
                      sessionStorage.removeItem("decarboniq_new_user");
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-500 transition-colors"
                    data-testid="welcome-go-to-data-btn">
                    Go to Step 2: Data Input <ArrowRight size={13} />
                  </button>
                  <button
                    onClick={() => { setShowWelcome(false); sessionStorage.removeItem("decarboniq_new_user"); }}
                    className="text-slate-600 hover:text-slate-400 p-1">
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 1 — Intent */}
            {activeStep === "intent" && (
              <StepSection id={1} stepKey="intent" number={1} title="Your Goal" icon={Zap}
                description="Tell us what you're working toward — shapes your results and narrative."
                footerActions={intent ? (
                  <button onClick={() => setActiveStep("data")}
                    className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700"
                    data-testid="next-to-data-btn">
                    Continue to Data Input <ArrowRight size={14} />
                  </button>
                ) : (
                  <p className="text-xs text-slate-400">Select your goal above to continue</p>
                )}>
                <Step1_Intent
                  journey={journeyData}
                  intent={intent}
                  onIntentSet={(k) => { setIntent(k); }}
                />
              </StepSection>
            )}

            {/* Step 2 — Data Input (fast, scope labels only) */}
            {activeStep === "data" && (
              <StepSection id={2} stepKey="data" number={2} title="Data Input" icon={BarChart2}
                description="Enter your annual activity data. Completes in under 60 seconds."
                footerActions={<>
                  <button onClick={() => calculateRef.current?.()}
                    disabled={busy}
                    className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                    data-testid="ws-calculate-btn">
                    {busy ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
                    {fp ? "Recalculate" : "Calculate"}
                  </button>
                  {fp && (
                    <button onClick={() => setActiveStep("result")} className="text-xs text-slate-500 hover:text-slate-700 font-semibold">
                      View Results →
                    </button>
                  )}
                </>}>
                <Step2_DataInput journey={journeyData} onCalculated={handleCalculated} calculateRef={calculateRef} />
              </StepSection>
            )}

            {/* Step 3 — Result & Readiness (merged: snapshot + CRS + benchmark) */}
            {activeStep === "result" && (
              <StepSection id={3} stepKey="result" number={3} title="Result & Readiness" icon={Activity}
                description="Your climate snapshot and financing readiness — in one view."
                footerActions={(journeyData?.footprint?.total_tco2e || fp) ? (
                  <button onClick={() => setActiveStep("actions")}
                    className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700">
                    See Your Actions <ArrowRight size={14} />
                  </button>
                ) : null}>
                <StepResult impact={impact} journeyData={journeyData} navigate={navigate} />
              </StepSection>
            )}

            {/* Step 4 — Actions (merged: opportunities + plan + CRS impact) */}
            {activeStep === "actions" && (
              <StepSection id={4} stepKey="actions" number={4} title="Actions" icon={Target}
                description="Ranked decarbonisation opportunities with financing readiness score impact."
                footerActions={(journeyData?.footprint?.total_tco2e || fp) ? (
                  <button onClick={() => setActiveStep("narrative")}
                    className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700">
                    See Finance Brief <ArrowRight size={14} />
                  </button>
                ) : null}>
                <StepActions actions={actions} cars={journeyData?.cars_score} />
              </StepSection>
            )}

            {/* Step 5 — Finance Narrative (hero output) */}
            {activeStep === "narrative" && (
              <StepSection id={5} stepKey="narrative" number={5} title="Finance Brief" icon={FileText}
                description="Your 30-second shareable output for banks, investors, and partners.">
                <StepNarrative journeyData={journeyData} navigate={navigate} />
              </StepSection>
            )}

            {/* Step 6 — Submit */}
            {activeStep === "submit" && (
              <StepSection id={6} stepKey="submit" number={6} title="Submit & Download" icon={DollarSign}
                description="Your one-click report and financing submission.">
                <StepSubmit journey={journeyData} navigate={navigate} />
              </StepSection>
            )}

            {/* Mobile step nav */}
            <div className="xl:hidden mt-4">
              <div className="flex flex-wrap gap-2">
                {STEPS.map(s => {
                  const done   = completedSteps.includes(s.key);
                  const active = activeStep === s.key;
                  return (
                    <button key={s.key} onClick={() => setActiveStep(s.key)}
                      className={`text-xs font-bold px-2.5 py-1.5 rounded-lg transition-all ${
                        active ? "bg-teal-600 text-white" :
                        done   ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                                 "bg-white border border-slate-200 text-slate-500"
                      }`}>
                      {s.id}. {s.label.split(" ")[0]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Insight Panel */}
        <LiveInsightPanel
          impact={{...impact, anomalies: anomaliesData?.anomalies || []}}
          submissionStatus={submissionStatus}
          eligibility={eligibility}
        />
      </div>
    </div>
  );
}
